import React, { useCallback, useEffect, useState } from 'react';
import { HandCoins } from 'lucide-react';
import {
  getCreatorDonations,
  CreatorDonationItem,
} from '../../../api/payment/creator-donation.api';
import MemberMark from '../../../components/membership/member-mark';

const PAGE_SIZE = 10;

const formatCa = (value: number) => `${Number(value).toLocaleString('vi-VN')} Ca`;

const formatTime = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

/**
 * Lịch sử donate công khai của creator: ai donate, lời nhắn, bao nhiêu Ca.
 * Người donate chọn ẩn danh đã được BE che tên sẵn (trả về "Người ẩn danh").
 */
const CreatorDonateLog: React.FC<{ userId?: string }> = ({ userId }) => {
  const [items, setItems] = useState<CreatorDonationItem[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const load = useCallback(
    async (pageNumber: number) => {
      if (!userId) return;
      setLoading(true);
      setFailed(false);
      try {
        const res = await getCreatorDonations(userId, pageNumber, PAGE_SIZE);
        setItems(res.items || []);
        setTotalPages(res.totalPages || 0);
        setTotalItems(res.totalItems || 0);
        setPage(pageNumber);
      } catch (e) {
        setFailed(true);
      } finally {
        setLoading(false);
      }
    },
    [userId]
  );

  useEffect(() => {
    load(1);
  }, [load]);

  if (!userId) return null;

  return (
    <div className="mt-8 box rounded-2xl p-4 md:p-6">
      <div className="flex items-center gap-2">
        <HandCoins className="text-blue-600" size={20} />
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
          Lịch sử donate
        </h3>
        {totalItems > 0 && (
          <span className="text-sm text-gray-500 dark:text-gray-400">
            ({totalItems.toLocaleString('vi-VN')} lượt)
          </span>
        )}
      </div>

      {loading && (
        <p className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
          Đang tải…
        </p>
      )}

      {!loading && failed && (
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Không tải được lịch sử donate.
          </p>
          <button
            type="button"
            onClick={() => load(page)}
            className="mt-2 text-sm font-medium text-blue-600 hover:underline"
          >
            Thử lại
          </button>
        </div>
      )}

      {!loading && !failed && items.length === 0 && (
        <p className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
          Chưa có lượt donate nào. Hãy là người đầu tiên ủng hộ!
        </p>
      )}

      {!loading && !failed && items.length > 0 && (
        <>
          <ul className="mt-4 divide-y divide-gray-100 dark:divide-slate-700">
            {items.map((item, idx) => (
              <li
                key={`${item.donatedAt}-${idx}`}
                className="flex items-start justify-between gap-4 py-3"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-gray-800 dark:text-white">
                    {item.donater}
                    <MemberMark creatorId={userId} userId={item.donaterUserId} />
                  </p>
                  {item.message ? (
                    <p className="mt-0.5 break-words text-sm text-gray-600 dark:text-gray-300">
                      {item.message}
                    </p>
                  ) : null}
                  {item.campaignTitle ? (
                    <span className="mt-1 inline-block max-w-full truncate rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-600 dark:bg-slate-700 dark:text-blue-300">
                      Ủng hộ chiến dịch: {item.campaignTitle}
                    </span>
                  ) : null}
                  <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">
                    {formatTime(item.donatedAt)}
                  </p>
                </div>
                <span className="shrink-0 whitespace-nowrap text-sm font-bold text-blue-600 dark:text-blue-400">
                  {formatCa(item.amount)}
                </span>
              </li>
            ))}
          </ul>

          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-center gap-3">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => load(page - 1)}
                className="rounded-lg border border-gray-200 px-3 py-1 text-sm disabled:opacity-40 dark:border-slate-600 dark:text-white"
              >
                Trước
              </button>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {page}/{totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => load(page + 1)}
                className="rounded-lg border border-gray-200 px-3 py-1 text-sm disabled:opacity-40 dark:border-slate-600 dark:text-white"
              >
                Sau
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CreatorDonateLog;
